import {z} from "zod";
import {useTranslation} from "react-i18next";
import {zodResolver} from "@hookform/resolvers/zod";
import {useForm} from "react-hook-form";
import {Grid} from "@mui/material";
import {Dialog} from "../base/Dialog";
import {UserPasswordForm, UserPasswordFormInputs} from "../users/UserPasswordForm";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {changeUserPassword} from "../../services/authenticationService";
import {swallowException} from "../../utils/appUtils";
import {getResourceErrorMessage} from "../../utils/resourceUtils";
import {validateUserPasswordRequirements} from "../../utils/userUtils";
import {PasswordIcon} from "../icons/PasswordIcon";
import {TFunction} from "i18next";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const UserPasswordChangeDialog: React.FC<Props> = ({open, onClose}) => {
    const {t} = useTranslation();
    const passwordManagement = useResourceManagement({changeUserPassword});
    const form = useForm({
        defaultValues: INIT_FORM_VALUES,
        resolver: zodResolver(getPasswordChangeSchema(t)),
    });

    const onSubmit = (formInputs: FormInputs) => {
        passwordManagement.reset();

        passwordManagement
            .changeUserPassword({oldPassword: formInputs.currentPassword, newPassword: formInputs.newPassword})
            .then(() => onClose(true))
            .catch(swallowException);
    };

    return (
        <Dialog
            title={t("dialogs.user_password_change.title")}
            titleIcon={
                <PasswordIcon
                    sx={{
                        stroke: "#404DB8",
                        width: "1.125rem",
                        height: "1.125rem",
                    }}
                />
            }
            submitTitle={t("dialogs.actions.save")}
            enableSubmit
            open={open}
            error={getResourceErrorMessage(passwordManagement.resource)}
            onClose={() => onClose(false)}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(onSubmit)}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <UserPasswordForm initFormValues={INIT_FORM_VALUES} form={form} />
                </Grid>
            </Grid>
        </Dialog>
    );
};

type FormInputs = UserPasswordFormInputs;

const getPasswordChangeSchema = (t: TFunction<"translation", undefined>) =>
    z
        .object({
            currentPassword: z.string().min(1),
            newPassword: z.string().min(1),
            newPasswordRepeat: z.string(),
        })
        .refine((data) => data.newPassword === data.newPasswordRepeat, {
            message: t("dialogs.user_password_change.passwords_dont_match"),
            path: ["newPasswordRepeat"],
        })
        .refine((data) => validateUserPasswordRequirements(data.newPassword), {
            message: t("dialogs.user_password_change.password_requirements"),
            path: ["newPassword"],
        });

const INIT_FORM_VALUES: FormInputs = {
    currentPassword: "",
    newPassword: "",
    newPasswordRepeat: "",
};
